import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, AlertCircle, FileText } from 'lucide-react';
import { format } from 'date-fns';

interface PayrollEntry {
  id: string;
  resource_id: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  earnings?: number;
  notes?: string;
  hourly_rate?: number | null;
}

interface PayrollChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: PayrollEntry | null;
  onSubmit: (requestData: any) => Promise<void>;
}

interface RequestFormData {
  clockIn: string;
  clockOut: string;
  breakStart: string;
  breakEnd: string;
  notes: string;
  requestReason: string;
}

export default function PayrollChangeRequestModal({ 
  isOpen, 
  onClose, 
  entry, 
  onSubmit 
}: PayrollChangeRequestModalProps) {
  const [formData, setFormData] = useState<RequestFormData>({
    clockIn: '',
    clockOut: '',
    breakStart: '',
    breakEnd: '',
    notes: '',
    requestReason: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to convert UTC timestamp to local timezone for form inputs
  const formatDateTimeForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    // Convert to local timezone for the form input
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  // Helper function to convert form datetime to UTC
  const convertToUTC = (dateTimeString: string): string | null => {
    if (!dateTimeString) return null;
    const localDate = new Date(dateTimeString);
    return localDate.toISOString();
  };

  // Initialize form data when entry changes
  useEffect(() => {
    if (entry) {
      setFormData({
        clockIn: formatDateTimeForInput(entry.clock_in),
        clockOut: formatDateTimeForInput(entry.clock_out),
        breakStart: formatDateTimeForInput(entry.break_start),
        breakEnd: formatDateTimeForInput(entry.break_end),
        notes: entry.notes || '',
        requestReason: ''
      });
      setError(null);
    }
  }, [entry]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate totals for preview
  const calculateTotals = () => {
    if (!formData.clockIn || !formData.clockOut) return { totalMinutes: 0, totalHours: 0, totalEarnings: 0 };

    const start = new Date(formData.clockIn);
    const end = new Date(formData.clockOut);
    let totalMs = end.getTime() - start.getTime();
    
    // Subtract break time if applicable
    if (formData.breakStart && formData.breakEnd) {
      const breakStart = new Date(formData.breakStart);
      const breakEnd = new Date(formData.breakEnd);
      totalMs -= (breakEnd.getTime() - breakStart.getTime());
    }
    
    const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
    const totalHours = totalMinutes / 60;
    const totalEarnings = totalHours * (entry?.hourly_rate || 0);
    
    return { totalMinutes, totalHours, totalEarnings };
  };

  const { totalMinutes, totalHours, totalEarnings } = calculateTotals();

  // Handle submit
  const handleSubmit = async () => {
    if (!entry) return;
    
    if (!formData.requestReason.trim()) {
      setError('Please provide a reason for the change request.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const requestData = {
        payroll_entry_id: entry.id,
        
        // Original data
        original_clock_in: entry.clock_in,
        original_clock_out: entry.clock_out,
        original_break_start: entry.break_start,
        original_break_end: entry.break_end,
        original_total_minutes: entry.total_minutes,
        original_total_earnings: entry.earnings,
        original_notes: entry.notes,
        
        // Requested changes
        requested_clock_in: convertToUTC(formData.clockIn),
        requested_clock_out: convertToUTC(formData.clockOut),
        requested_break_start: convertToUTC(formData.breakStart),
        requested_break_end: convertToUTC(formData.breakEnd),
        requested_notes: formData.notes || null,
        request_reason: formData.requestReason
      };

      await onSubmit(requestData);
      onClose();
    } catch (err) {
      console.error('Error submitting change request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit change request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !entry) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Request Time Change</h3>
              <p className="text-sm text-gray-600 mt-1">
                Submit a request to modify your time entry
              </p>
              <div className="flex items-center gap-2 mt-2">
                <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  CHANGE REQUEST
                </div>
                <span className="text-xs text-gray-500">
                  {entry.clock_in ? format(new Date(entry.clock_in), 'MMM d, yyyy') : ''}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Form Content */}
        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Current vs Requested Comparison */}
          <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Current Entry
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Clock In:</span>
                <div className="font-medium">{entry.clock_in ? format(new Date(entry.clock_in), 'h:mm a') : '-'}</div>
              </div>
              <div>
                <span className="text-gray-500">Clock Out:</span>
                <div className="font-medium">{entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : '-'}</div>
              </div>
              <div>
                <span className="text-gray-500">Total Time:</span>
                <div className="font-medium">{Math.floor((entry.total_minutes || 0) / 60)}h {(entry.total_minutes || 0) % 60}m</div>
              </div>
              <div>
                <span className="text-gray-500">Earnings:</span>
                <div className="font-bold text-green-600">${(entry.earnings || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Requested Changes */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Requested Changes</h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Clock In
                </label>
                <input
                  type="datetime-local"
                  name="clockIn"
                  value={formData.clockIn}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Clock Out
                </label>
                <input
                  type="datetime-local"
                  name="clockOut"
                  value={formData.clockOut}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Break Start</label>
                <input
                  type="datetime-local"
                  name="breakStart"
                  value={formData.breakStart}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Break End</label>
                <input
                  type="datetime-local"
                  name="breakEnd"
                  value={formData.breakEnd}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                placeholder="Optional notes..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                Reason for Change Request *
              </label>
              <textarea
                name="requestReason"
                value={formData.requestReason}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 bg-white/60 backdrop-blur-sm"
                placeholder="Please explain why you need to change this time entry..."
              />
            </div>
          </div>

          {/* Live calculation preview */}
          {formData.clockIn && formData.clockOut && (
            <div className="bg-blue-50/80 backdrop-blur-sm rounded-xl p-4 border border-blue-200/50">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Requested Changes Preview</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-600">Total Time:</span>
                  <div className="font-medium text-blue-900">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</div>
                </div>
                <div>
                  <span className="text-blue-600">Total Earnings:</span>
                  <div className="font-bold text-blue-900">${totalEarnings.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.requestReason.trim()}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="h-4 w-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting Request...
                </div>
              ) : (
                'Submit Change Request'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}